<script setup lang="ts">
import InputGroup from 'primevue/inputgroup';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import ExternalIcon from '~/components/design-system/icons/ExternalIcon.vue';
import InputText from 'primevue/inputtext';
import InputGroupAddon from 'primevue/inputgroupaddon';
import { useDebounceFn } from '@vueuse/core';
import type { ISearchResponse } from '~/interfaces/dto/games/response';
import SearchTabs from '~/components/user-interface/search/search-tabs.vue';
import GenericLoader from '~/components/user-interface/generic-loader.vue';
import GenericError from '~/components/user-interface/generic-error.vue';

const { $gameService } = useServices();
const { $trackEvent } = useNuxtApp();
const gameStore = useGameStore();
const personalised = usePersonalised();

const searchText = ref<string>('');
const searchInput = ref<HTMLElement>();
const searching = ref<boolean>(false);
const error = ref<boolean>(false);
const results = ref<ISearchResponse>();

function reset() {
  searchText.value = '';
  results.value = undefined;
  error.value = false;
}

async function fetchSearchedGames() {
  if (results.value?.total === 0) error.value = false;
  const query = searchText.value.trim();
  if (query.length < 3) return;
  searching.value = true;
  $trackEvent('SearchEvent', 'SEARCH', {
    search_query: query,
  });

  results.value = await $gameService.fetchSearchedGames({
    search: query,
    skip: 0,
    limit: 0,
  });

  if (results.value?.total === 0) error.value = true;
  else {
    personalised.updatePersonalisedData('recentSearches', query, true, 'Local');
  }
  searching.value = false;
}

const debounceSearch = useDebounceFn(() => {
  fetchSearchedGames();
}, 500);

const defaultResults = computed(() => {
  const defaults = [];
  if (!!gameStore.returnRecommended?.length) {
    defaults.push({
      count: gameStore.returnRecommended?.length,
      data: gameStore.returnRecommended,
      vertical: 'Recommended',
    });
  }

  if (!!gameStore.returnMostLiked?.length) {
    defaults.push({
      count: gameStore.mostLiked?.length,
      data: gameStore.mostLiked,
      vertical: 'MostLiked',
    });
  }

  if (!!gameStore.returnFavorites?.length)
    defaults.push({
      count: gameStore.returnFavorites?.length,
      data: gameStore.returnFavorites,
      vertical: 'Favorites',
    });

  return defaults;
});

const clearRecentSearches = () =>
  personalised.setPersonalisedData('recentSearches', [], 'Local');

const removeRecentSearch = (query: string) =>
  personalised.updatePersonalisedData('recentSearches', query, false, 'Local');

const totalGames = computed(() => {
  if (!!results.value) return results.value.total;
  return defaultResults.value.reduce((acc, item) => acc + item.count, 0);
});

const displayTabs = computed(() => {
  if (searching.value) return false;
  if (!!searchText.value && !totalGames.value) return false;
  return !!totalGames.value;
});

const displayRecentSearches = computed(
  () => gameStore.returnRecentSearches.length > 0 && !searchText.value,
);

onMounted(() => {
  document.getElementById('search')?.focus();
  $trackEvent('SearchEvent', 'TOGGLE_SEARCH');
});

watchEffect(() => {
  if (searchText.value?.length > 2) debounceSearch();
  if (!searchText.value?.length) reset();
});
</script>

<template>
  <div class="p-2 relative">
    <InputGroup>
      <InputGroupAddon class="border-r">
        <ExternalIcon class="w-6 mx-auto" icon-name="search-magnifying" />
      </InputGroupAddon>
      <InputText
        id="search"
        ref="searchInput"
        v-model="searchText"
        :placeholder="$t(`search-games`)"
        :label="$t(`search-games`)"
        class="border-collapse"
      />
      <InputGroupAddon
        v-if="!!searchText"
        class="bg-layer text-base bg-stacked"
        @click="reset"
      >
        <div class="rounded-full bg-stacked p-1 cursor-pointer">
          <XMarkIcon class="w-5" />
        </div>
      </InputGroupAddon>
    </InputGroup>
    <div v-if="displayRecentSearches" class="mt-4 overflow-auto">
      <div class="flex justify-between items-center mb-0.5">
        <p class="text-base-priority text-xs font-bold leading-6 capitalize">
          {{ $t('recent-searches') }}
        </p>
        <div
          class="bg-layer-1 rounded-lg px-1.5 text-base-priority text-xs leading-6 cursor-pointer"
          @click="clearRecentSearches()"
        >
          {{ $t('clear-all') }}
        </div>
      </div>
      <div class="flex gap-2 w-full overflow-x-auto pb-2">
        <div
          v-for="(search, index) in gameStore.returnRecentSearches"
          :key="index"
          class="bg-layer-2 rounded-lg px-2 flex justify-between items-center cursor-pointer"
          @click="searchText = search"
        >
          <p class="text-base-priority text-xs mr-4 leading-6 text-nowrap">
            {{ search }}
          </p>
          <XMarkIcon
            class="w-4 h-4 text-base-color stroke-2"
            @click.stop="removeRecentSearch(search)"
          />
        </div>
      </div>
    </div>
    <SearchTabs
      v-if="displayTabs"
      :search-tabs="results?.verticals || defaultResults"
      :total-results="totalGames"
      :search-query="searchText"
      :class="{ 'mt-2': !displayRecentSearches }"
    />
    <GenericLoader v-show="searching" />
    <GenericError v-show="error">
      {{ $t('sorry-cant-find-results') }}
    </GenericError>
  </div>
</template>

<style scoped lang="scss"></style>
